import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Monitor,
  Server,
  Microscope,
  Smartphone,
  Activity,
  Award,
  Users,
  Briefcase,
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Code2,
  Database,
  Brain,
  Layers,
  Zap,
} from "lucide-react";

import TechCarousel from "./TechCarousel";
import { tracks } from "./trackData";
import aim4uLogo from "../../images/aim4u_logo.jpg";

/* ================= SCROLL REVEAL HOOK ================= */
const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

/* ================= ANIMATED BUTTON ================= */
const Button = ({ children, onClick, className = "", variant = "default", ...props }) => {
  const baseClasses = "inline-flex items-center justify-center gap-2 rounded-2xl font-bold cursor-pointer";

  const variants = {
    default: "track-tab text-foreground px-6 py-3",
    gradient: "btn-gradient text-white px-8 py-4 text-lg",
    ghost: "bg-transparent hover:bg-muted/50 text-muted-foreground px-4 py-3 rounded-full",
  };

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

/* ================= ICON MAP ================= */
const iconMap = {
  Monitor: <Monitor className="h-5 w-5" />,
  Server: <Server className="h-5 w-5" />,
  Microscope: <Microscope className="h-5 w-5" />,
  Smartphone: <Smartphone className="h-5 w-5" />,
  Brain: <Brain className="h-5 w-5" />,
  Activity: <Activity className="h-5 w-5" />,
};

/* ================= INTERACTIVE MILESTONE VISUAL ================= */
const MilestoneVisual = ({ index, isVisible }) => {
  const containerRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const [isHovered, setIsHovered] = useState(false);
  const rafRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMouse({
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      });
    });
  }, []);

  const px = (mouse.x - 0.5) * 2;
  const py = (mouse.y - 0.5) * 2;

  const visuals = [
    /* === 01: CODE EDITOR — typing cursor, syntax blocks, browser preview === */
    <svg viewBox="0 0 260 200" className="w-full h-full">
      {/* Editor window */}
      <g style={{ transform: `translate(${px * 3}px, ${py * 3}px)` }} className="vis-layer-1">
        <rect x="10" y="10" width="140" height="120" rx="8" fill="none" stroke="hsl(228,50%,36%)" strokeWidth="1.5" opacity="0.6" />
        <rect x="10" y="10" width="140" height="18" rx="8" fill="hsl(228,50%,36%)" opacity="0.15" />
        <circle cx="22" cy="19" r="3" fill="#ff5f57" /><circle cx="32" cy="19" r="3" fill="#febc2e" /><circle cx="42" cy="19" r="3" fill="#28c840" />
        {/* Code lines */}
        <rect x="20" y="38" width="50" height="4" rx="2" fill="hsl(228,50%,36%)" opacity="0.4" />
        <rect x="30" y="48" width="70" height="4" rx="2" fill="hsl(97,52%,49%)" opacity="0.5" />
        <rect x="30" y="58" width="55" height="4" rx="2" fill="hsl(228,50%,36%)" opacity="0.3" />
        <rect x="40" y="68" width="80" height="4" rx="2" fill="hsl(97,52%,49%)" opacity="0.4" />
        <rect x="40" y="78" width="60" height="4" rx="2" fill="hsl(228,50%,36%)" opacity="0.35" />
        <rect x="30" y="88" width="45" height="4" rx="2" fill="hsl(97,52%,49%)" opacity="0.3" />
        <rect x="20" y="98" width="35" height="4" rx="2" fill="hsl(228,50%,36%)" opacity="0.4" />
        {/* Typing cursor */}
        <rect x="75" y="98" width="2" height="10" rx="1" fill="hsl(97,52%,49%)" className="animate-cursor-blink" />
      </g>
      {/* Browser preview */}
      <g style={{ transform: `translate(${px * -5}px, ${py * -4}px)` }} className="vis-layer-2">
        <rect x="120" y="50" width="130" height="100" rx="8" fill="none" stroke="hsl(97,52%,49%)" strokeWidth="1.5" opacity="0.5" />
        <rect x="120" y="50" width="130" height="16" rx="8" fill="hsl(97,52%,49%)" opacity="0.1" />
        <rect x="130" y="55" width="50" height="6" rx="3" fill="hsl(228,50%,36%)" opacity="0.15" />
        {/* Preview content blocks */}
        <rect x="130" y="75" width="40" height="6" rx="2" fill="hsl(228,50%,36%)" opacity="0.25" />
        <rect x="130" y="87" width="100" height="20" rx="4" fill="hsl(97,52%,49%)" opacity="0.08" />
        <rect x="135" y="92" width="60" height="3" rx="1" fill="hsl(97,52%,49%)" opacity="0.3" />
        <rect x="135" y="99" width="45" height="3" rx="1" fill="hsl(97,52%,49%)" opacity="0.2" />
        <rect x="130" y="115" width="50" height="14" rx="7" fill="hsl(97,52%,49%)" opacity="0.2" />
        <rect x="190" y="115" width="50" height="14" rx="7" fill="hsl(228,50%,36%)" opacity="0.15" />
      </g>
      {/* Floating particles */}
      <circle cx="80" cy="140" r="3" fill="hsl(97,52%,49%)" opacity="0.3" className="animate-float-particle" />
      <circle cx="200" cy="30" r="2" fill="hsl(228,50%,36%)" opacity="0.4" className="animate-float-particle-2" />
      <circle cx="160" cy="170" r="2.5" fill="hsl(97,52%,49%)" opacity="0.25" className="animate-float-particle-3" />
    </svg>,

    /* === 02: COMPONENT TREE — React-like node graph === */
    <svg viewBox="0 0 260 200" className="w-full h-full">
      {/* Connection lines */}
      <g style={{ transform: `translate(${px * 2}px, ${py * 2}px)` }} className="vis-layer-1">
        <line x1="130" y1="35" x2="70" y2="80" stroke="hsl(97,52%,49%)" strokeWidth="1.5" opacity="0.3" strokeDasharray="4 3" className="animate-dash-flow" />
        <line x1="130" y1="35" x2="190" y2="80" stroke="hsl(228,50%,36%)" strokeWidth="1.5" opacity="0.3" strokeDasharray="4 3" className="animate-dash-flow" />
        <line x1="70" y1="100" x2="40" y2="145" stroke="hsl(97,52%,49%)" strokeWidth="1" opacity="0.25" strokeDasharray="3 3" className="animate-dash-flow-2" />
        <line x1="70" y1="100" x2="100" y2="145" stroke="hsl(228,50%,36%)" strokeWidth="1" opacity="0.25" strokeDasharray="3 3" className="animate-dash-flow-2" />
        <line x1="190" y1="100" x2="160" y2="145" stroke="hsl(228,50%,36%)" strokeWidth="1" opacity="0.25" strokeDasharray="3 3" className="animate-dash-flow-2" />
        <line x1="190" y1="100" x2="220" y2="145" stroke="hsl(97,52%,49%)" strokeWidth="1" opacity="0.25" strokeDasharray="3 3" className="animate-dash-flow-2" />
      </g>
      {/* Nodes - Layer 2 with parallax */}
      <g style={{ transform: `translate(${px * -4}px, ${py * -3}px)` }} className="vis-layer-2">
        {/* Root */}
        <rect x="105" y="15" width="50" height="28" rx="6" fill="hsl(228,50%,36%)" opacity={isHovered ? 0.6 : 0.35} className="vis-node-glow" />
        <text x="130" y="33" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold" opacity="0.9">Internship</text>
        {/* Level 2 */}
        <rect x="45" y="75" width="50" height="28" rx="6" fill="hsl(97,52%,49%)" opacity={isHovered ? 0.55 : 0.3} className="vis-node-glow" />
        <text x="70" y="93" textAnchor="middle" fill="white" fontSize="7" fontWeight="600" opacity="0.9">Learning</text>
        <rect x="165" y="75" width="50" height="28" rx="6" fill="hsl(97,52%,49%)" opacity={isHovered ? 0.55 : 0.3} className="vis-node-glow" />
        <text x="190" y="93" textAnchor="middle" fill="white" fontSize="7" fontWeight="600" opacity="0.9">Projects</text>
        {/* Level 3 */}
        <rect x="15" y="140" width="50" height="24" rx="5" fill="hsl(228,50%,36%)" opacity="0.25" />
        <text x="40" y="155" textAnchor="middle" fill="hsl(228,50%,36%)" fontSize="6" fontWeight="500" opacity="0.7">Topics</text>
        <rect x="75" y="140" width="50" height="24" rx="5" fill="hsl(228,50%,36%)" opacity="0.25" />
        <text x="100" y="155" textAnchor="middle" fill="hsl(228,50%,36%)" fontSize="6" fontWeight="500" opacity="0.7">Guidance</text>
        <rect x="135" y="140" width="50" height="24" rx="5" fill="hsl(97,52%,49%)" opacity="0.2" />
        <text x="160" y="155" textAnchor="middle" fill="hsl(97,52%,49%)" fontSize="6" fontWeight="500" opacity="0.7">Tasks</text>
        <rect x="195" y="140" width="50" height="24" rx="5" fill="hsl(97,52%,49%)" opacity="0.2" />
        <text x="220" y="155" textAnchor="middle" fill="hsl(97,52%,49%)" fontSize="6" fontWeight="500" opacity="0.7">Review</text>
      </g>
      {/* Data flow particles */}
      <circle r="3" fill="hsl(97,52%,49%)" opacity="0.6" className="animate-data-flow-1" />
      <circle r="2.5" fill="hsl(228,50%,36%)" opacity="0.5" className="animate-data-flow-2" />
    </svg>,

    /* === 03: MOBILE DEVICE — phone with UI elements === */
    <svg viewBox="0 0 260 200" className="w-full h-full">
      {/* Phone body */}
      <g style={{ transform: `translate(${px * 4}px, ${py * 3}px)` }} className="vis-layer-1">
        <rect x="80" y="8" width="100" height="184" rx="16" fill="none" stroke="hsl(228,50%,36%)" strokeWidth="2" opacity="0.4" />
        <rect x="115" y="14" width="30" height="4" rx="2" fill="hsl(228,50%,36%)" opacity="0.2" />
        {/* Screen content */}
        <rect x="88" y="28" width="84" height="150" rx="4" fill="hsl(228,50%,36%)" opacity="0.04" />
        {/* Status bar */}
        <rect x="92" y="32" width="20" height="3" rx="1" fill="hsl(228,50%,36%)" opacity="0.2" />
        <rect x="148" y="32" width="20" height="3" rx="1" fill="hsl(228,50%,36%)" opacity="0.2" />
      </g>
      {/* UI Elements inside phone - parallax layer */}
      <g style={{ transform: `translate(${px * -3}px, ${py * -2}px)` }} className="vis-layer-2">
        {/* Header image */}
        <rect x="92" y="42" width="76" height="30" rx="4" fill="hsl(97,52%,49%)" opacity="0.12" />
        <circle cx="130" cy="57" r="8" fill="hsl(97,52%,49%)" opacity="0.2" />
        {/* Cards */}
        <rect x="92" y="78" width="76" height="22" rx="4" fill="hsl(228,50%,36%)" opacity="0.08" className="animate-card-slide-1" />
        <rect x="96" y="83" width="30" height="3" rx="1" fill="hsl(228,50%,36%)" opacity="0.3" />
        <rect x="96" y="90" width="50" height="3" rx="1" fill="hsl(228,50%,36%)" opacity="0.15" />

        <rect x="92" y="104" width="76" height="22" rx="4" fill="hsl(97,52%,49%)" opacity="0.08" className="animate-card-slide-2" />
        <rect x="96" y="109" width="35" height="3" rx="1" fill="hsl(97,52%,49%)" opacity="0.3" />
        <rect x="96" y="116" width="45" height="3" rx="1" fill="hsl(97,52%,49%)" opacity="0.15" />

        <rect x="92" y="130" width="76" height="22" rx="4" fill="hsl(228,50%,36%)" opacity="0.08" className="animate-card-slide-3" />
        <rect x="96" y="135" width="25" height="3" rx="1" fill="hsl(228,50%,36%)" opacity="0.3" />
        <rect x="96" y="142" width="55" height="3" rx="1" fill="hsl(228,50%,36%)" opacity="0.15" />
        {/* Bottom nav */}
        <rect x="92" y="158" width="76" height="16" rx="4" fill="hsl(228,50%,36%)" opacity="0.06" />
        <circle cx="108" cy="166" r="4" fill="hsl(228,50%,36%)" opacity="0.15" />
        <circle cx="130" cy="166" r="4" fill="hsl(97,52%,49%)" opacity="0.3" />
        <circle cx="152" cy="166" r="4" fill="hsl(228,50%,36%)" opacity="0.15" />
      </g>
      {/* Floating gesture indicators */}
      <g style={{ transform: `translate(${px * -6}px, ${py * -5}px)` }}>
        <circle cx="200" cy="60" r="6" fill="none" stroke="hsl(97,52%,49%)" strokeWidth="1" opacity="0.3" className="animate-tap-ripple" />
        <circle cx="200" cy="60" r="12" fill="none" stroke="hsl(97,52%,49%)" strokeWidth="0.5" opacity="0.15" className="animate-tap-ripple-2" />
        <path d="M45 100 Q55 80 50 60" fill="none" stroke="hsl(228,50%,36%)" strokeWidth="1.5" opacity="0.2" strokeDasharray="3 3" className="animate-swipe" />
      </g>
    </svg>,

    /* === 04: TESTING — checklist with animated checks, bug icon === */
    <svg viewBox="0 0 260 200" className="w-full h-full">
      {/* Test document */}
      <g style={{ transform: `translate(${px * 3}px, ${py * 2}px)` }} className="vis-layer-1">
        <rect x="60" y="15" width="140" height="170" rx="8" fill="none" stroke="hsl(228,50%,36%)" strokeWidth="1.5" opacity="0.3" />
        <rect x="60" y="15" width="140" height="22" rx="8" fill="hsl(228,50%,36%)" opacity="0.08" />
        <rect x="70" y="22" width="60" height="5" rx="2" fill="hsl(228,50%,36%)" opacity="0.25" />
      </g>
      {/* Checklist items */}
      <g style={{ transform: `translate(${px * -4}px, ${py * -3}px)` }} className="vis-layer-2">
        {/* Item 1 - checked */}
        <rect x="72" y="48" width="14" height="14" rx="3" fill="hsl(97,52%,49%)" opacity="0.25" />
        <path d="M76 55 L79 58 L84 52" fill="none" stroke="hsl(97,52%,49%)" strokeWidth="2" strokeLinecap="round" opacity="0.7" className="animate-check-draw-1" />
        <rect x="92" y="52" width="80" height="4" rx="2" fill="hsl(228,50%,36%)" opacity="0.2" />

        {/* Item 2 - checked */}
        <rect x="72" y="72" width="14" height="14" rx="3" fill="hsl(97,52%,49%)" opacity="0.25" />
        <path d="M76 79 L79 82 L84 76" fill="none" stroke="hsl(97,52%,49%)" strokeWidth="2" strokeLinecap="round" opacity="0.7" className="animate-check-draw-2" />
        <rect x="92" y="76" width="65" height="4" rx="2" fill="hsl(228,50%,36%)" opacity="0.2" />

        {/* Item 3 - checking animation */}
        <rect x="72" y="96" width="14" height="14" rx="3" fill="hsl(97,52%,49%)" opacity="0.15" className="animate-check-bg" />
        <path d="M76 103 L79 106 L84 100" fill="none" stroke="hsl(97,52%,49%)" strokeWidth="2" strokeLinecap="round" opacity="0.6" className="animate-check-draw-3" />
        <rect x="92" y="100" width="90" height="4" rx="2" fill="hsl(228,50%,36%)" opacity="0.2" />

        {/* Item 4 - pending */}
        <rect x="72" y="120" width="14" height="14" rx="3" fill="none" stroke="hsl(228,50%,36%)" strokeWidth="1" opacity="0.2" />
        <rect x="92" y="124" width="70" height="4" rx="2" fill="hsl(228,50%,36%)" opacity="0.12" />

        {/* Item 5 - pending */}
        <rect x="72" y="144" width="14" height="14" rx="3" fill="none" stroke="hsl(228,50%,36%)" strokeWidth="1" opacity="0.2" />
        <rect x="92" y="148" width="55" height="4" rx="2" fill="hsl(228,50%,36%)" opacity="0.12" />
      </g>
      {/* Progress bar */}
      <g style={{ transform: `translate(${px * -2}px, ${py * -1}px)` }}>
        <rect x="72" y="170" width="116" height="6" rx="3" fill="hsl(228,50%,36%)" opacity="0.08" />
        <rect x="72" y="170" width="70" height="6" rx="3" fill="hsl(97,52%,49%)" opacity="0.35" className="animate-progress-fill" />
      </g>
      {/* Bug icon */}
      <g style={{ transform: `translate(${px * -6}px, ${py * -5}px)` }}>
        <circle cx="220" cy="50" r="12" fill="hsl(228,50%,36%)" opacity="0.08" />
        <text x="220" y="55" textAnchor="middle" fontSize="14" className="animate-bug-bounce">🐛</text>
        <line x1="220" y1="62" x2="220" y2="75" stroke="hsl(97,52%,49%)" strokeWidth="1" opacity="0.3" strokeDasharray="2 2" />
        <text x="220" y="85" textAnchor="middle" fontSize="10" className="animate-bug-fix">✓</text>
      </g>
    </svg>,

    /* === 05: DEPLOYMENT — rocket, cloud, CI/CD pipeline === */
    <svg viewBox="0 0 260 200" className="w-full h-full">
      {/* Cloud shapes */}
      <g style={{ transform: `translate(${px * 3}px, ${py * 2}px)` }} className="vis-layer-1">
        <ellipse cx="180" cy="45" rx="35" ry="18" fill="hsl(228,50%,36%)" opacity="0.07" className="animate-cloud-drift" />
        <ellipse cx="200" cy="42" rx="25" ry="14" fill="hsl(228,50%,36%)" opacity="0.05" className="animate-cloud-drift" />
        <ellipse cx="70" cy="35" rx="28" ry="15" fill="hsl(97,52%,49%)" opacity="0.06" className="animate-cloud-drift-2" />
      </g>
      {/* Rocket */}
      <g style={{ transform: `translate(${px * -5}px, ${py * -6}px)` }} className="vis-layer-2 animate-rocket-hover">
        <path d="M130 30 C130 30 120 50 120 70 L140 70 C140 50 130 30 130 30Z" fill="hsl(228,50%,36%)" opacity="0.35" />
        <rect x="123" y="55" width="14" height="6" rx="2" fill="hsl(97,52%,49%)" opacity="0.4" />
        <circle cx="130" cy="48" r="4" fill="hsl(97,52%,49%)" opacity="0.3" />
        {/* Fins */}
        <path d="M120 65 L112 75 L120 72Z" fill="hsl(228,50%,36%)" opacity="0.25" />
        <path d="M140 65 L148 75 L140 72Z" fill="hsl(228,50%,36%)" opacity="0.25" />
        {/* Exhaust */}
        <ellipse cx="130" cy="78" rx="6" ry="4" fill="hsl(97,52%,49%)" opacity="0.3" className="animate-exhaust" />
        <ellipse cx="130" cy="85" rx="4" ry="3" fill="hsl(97,52%,49%)" opacity="0.15" className="animate-exhaust-2" />
      </g>
      {/* Pipeline */}
      <g style={{ transform: `translate(${px * 2}px, ${py * 3}px)` }}>
        {/* Pipeline steps */}
        <rect x="30" y="120" width="40" height="28" rx="6" fill="hsl(228,50%,36%)" opacity="0.12" />
        <text x="50" y="137" textAnchor="middle" fill="hsl(228,50%,36%)" fontSize="7" fontWeight="600" opacity="0.5">Build</text>
        <path d="M70 134 L85 134" stroke="hsl(97,52%,49%)" strokeWidth="1.5" opacity="0.3" markerEnd="url(#arrowG)" />

        <rect x="85" y="120" width="40" height="28" rx="6" fill="hsl(97,52%,49%)" opacity="0.12" />
        <text x="105" y="137" textAnchor="middle" fill="hsl(97,52%,49%)" fontSize="7" fontWeight="600" opacity="0.5">Test</text>
        <path d="M125 134 L140 134" stroke="hsl(97,52%,49%)" strokeWidth="1.5" opacity="0.3" markerEnd="url(#arrowG)" />

        <rect x="140" y="120" width="40" height="28" rx="6" fill="hsl(228,50%,36%)" opacity="0.12" />
        <text x="160" y="137" textAnchor="middle" fill="hsl(228,50%,36%)" fontSize="7" fontWeight="600" opacity="0.5">Stage</text>
        <path d="M180 134 L195 134" stroke="hsl(97,52%,49%)" strokeWidth="1.5" opacity="0.3" markerEnd="url(#arrowG)" />

        <rect x="195" y="120" width="40" height="28" rx="6" fill="hsl(97,52%,49%)" opacity={isHovered ? 0.3 : 0.12} className="vis-node-glow" />
        <text x="215" y="137" textAnchor="middle" fill="hsl(97,52%,49%)" fontSize="7" fontWeight="bold" opacity="0.6">Deploy</text>

        {/* Flow particle */}
        <circle r="3" fill="hsl(97,52%,49%)" opacity="0.5" className="animate-pipeline-flow" />
      </g>
      {/* Arrow marker */}
      <defs>
        <marker id="arrowG" viewBox="0 0 6 6" refX="5" refY="3" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0 0 L6 3 L0 6Z" fill="hsl(97,52%,49%)" opacity="0.4" />
        </marker>
      </defs>
      {/* Sparkle particles */}
      <circle cx="50" cy="80" r="2" fill="hsl(97,52%,49%)" opacity="0.3" className="animate-float-particle" />
      <circle cx="230" cy="100" r="2.5" fill="hsl(228,50%,36%)" opacity="0.2" className="animate-float-particle-2" />
    </svg>,
  ];

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setMouse({ x: 0.5, y: 0.5 }); }}
      className="milestone-visual-container w-full h-full flex items-center justify-center"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "scale(1)" : "scale(0.85)",
        transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {visuals[index % visuals.length]}
    </div>
  );
};

/* ================= PARTICLE BACKGROUND ================= */
const ParticleBackground = () => {
  const particles = useMemo(() =>
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      size: 2 + Math.random() * 4,
      delay: Math.random() * 8,
      duration: 8 + Math.random() * 4,
    })),
    []
  );

  return (
    <div className="particle-container">
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: p.left,
            width: p.size,
            height: p.size,
            animationDelay: `${-p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

/* ================= MILESTONE CARD ================= */
const MilestoneCard = ({ milestone, index, isLast, trackColor }) => {
  const [ref, isVisible] = useScrollReveal(0.12);
  const delay = index * 100;

  return (
    <div
      ref={ref}
      className="relative flex items-stretch gap-3.5 sm:gap-5 md:gap-7"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(24px)",
        transition: `all 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms`,
      }}
    >
      {/* Timeline Node Column with connecting line */}
      <div className="relative flex flex-col items-center flex-shrink-0 pt-1">
        <div className="timeline-node h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 flex items-center justify-center rounded-full text-white font-display font-black text-base sm:text-lg md:text-xl shadow-lg border-2 border-white z-10 flex-shrink-0">
          {milestone.number}
        </div>
        {!isLast && (
          <div className="w-1 flex-1 bg-gradient-to-b from-brand-blue to-brand-green my-2 rounded-full min-h-[36px] opacity-75 shadow-sm" />
        )}
      </div>

      {/* Content Card with Visual */}
      <div className={`flex-1 min-w-0 ${!isLast ? "pb-6 sm:pb-8" : "pb-2"}`}>
        <div className="glass-card rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 relative border border-slate-200/80 hover:border-emerald-300 transition-all duration-300 shadow-sm hover:shadow-md bg-white/95 backdrop-blur-md">
          <div className="flex flex-col-reverse sm:flex-row items-start sm:items-center justify-between gap-4 md:gap-6">
            {/* Text content */}
            <div className="flex-1 min-w-0 text-left">
              {/* Week Label Pill */}
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200/60 mb-2">
                <span>{milestone.weekLabel}</span>
              </div>

              {/* Title */}
              <h4 className="font-display text-base sm:text-lg md:text-xl font-bold text-slate-800 tracking-tight mb-2.5">
                {milestone.title}
              </h4>

              {/* Topics (clean 2-column grid on sm+ screens) */}
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {milestone.topics.map((topic, j) => (
                  <li
                    key={j}
                    className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-600 font-medium leading-snug"
                  >
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>{topic}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Small compact interactive visual */}
            <div className="w-28 sm:w-32 md:w-36 lg:w-40 h-20 sm:h-24 md:h-26 flex-shrink-0 self-center sm:self-auto flex items-center justify-center p-2 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100/90 border border-slate-200/70 shadow-inner overflow-hidden">
              <MilestoneVisual index={index} isVisible={isVisible} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================= ROADMAP TIMELINE ================= */
const RoadmapTimeline = ({ milestones, trackId, color }) => {
  return (
    <div className="relative mx-auto max-w-4xl py-6 md:py-8 px-3 sm:px-6">
      {/* Milestones */}
      <div className="flex flex-col">
        {milestones.map((m, i) => (
          <MilestoneCard
            key={`${trackId}-${m.number}`}
            milestone={m}
            index={i}
            isLast={i === milestones.length - 1}
            trackColor={color}
          />
        ))}
      </div>
    </div>
  );
};

/* ================= FEATURE CARD ================= */
const FeatureCard = ({ icon, label, delay }) => {
  const [ref, isVisible] = useScrollReveal(0.2);

  return (
    <div
      ref={ref}
      className="feature-card rounded-2xl p-6 text-center backdrop-blur-sm"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0) scale(1)" : "translateY(30px) scale(0.9)",
        transition: `all 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms`,
      }}
    >
      <div className="feature-icon inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-brand-green/20 to-brand-blue/20 text-brand-green mb-4">
        {icon}
      </div>
      <div className="text-sm font-semibold text-foreground relative z-10">{label}</div>
    </div>
  );
};

/* ================= MAIN COMPONENT ================= */
const InternshipTracks = () => {
  const [activeTrack, setActiveTrack] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [headerRef, headerVisible] = useScrollReveal(0.1);
  const [ctaRef, ctaVisible] = useScrollReveal(0.2);

  const switchTrack = (i) => {
    if (i === activeTrack || isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveTrack(i);
      setIsTransitioning(false);
    }, 300);
  };

  const nextTrack = () => switchTrack((activeTrack + 1) % tracks.length);
  const prevTrack = () => switchTrack((activeTrack - 1 + tracks.length) % tracks.length);

  const track = tracks[activeTrack];

  const features = [
    { icon: <GraduationCap className="h-6 w-6" />, label: "Mentor Guidance" },
    { icon: <Award className="h-6 w-6" />, label: "Certificate Policy Applies" },
    { icon: <Briefcase className="h-6 w-6" />, label: "Assigned Projects" },
    { icon: <Users className="h-6 w-6" />, label: "Learning Activities" },
  ];

  return (
    <section className="organic-bg min-h-screen relative">
      {/* Animated background overlays */}
      <div className="floating-orb orb-1" />
      <div className="floating-orb orb-2" />
      <div className="floating-orb orb-3" />
      <div className="floating-orb orb-4" />
      <div className="mesh-overlay" />
      <div className="circuit-pattern" />

      {/* Particle effect */}
      <ParticleBackground />

      {/* HEADER */}
      <div
        ref={headerRef}
        className="relative z-10 text-center pt-10 pb-6 px-4"
        style={{
          opacity: headerVisible ? 1 : 0,
          transform: headerVisible ? "translateY(0)" : "translateY(30px)",
          transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <img
          src={aim4uLogo}
          alt="AIM4U Logo"
          className="mx-auto mb-4 h-16 drop-shadow-md animate-float"
        />

        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-foreground mb-3">
          Explore{" "}
          <span className="text-gradient">Internship Tracks</span>
        </h2>

        <p className="mx-auto max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
          12-week internship tracks with structured learning milestones and project activities
        </p>
      </div>

      {/* TRACK TABS */}
      <div className="relative z-10 flex justify-center flex-wrap gap-2.5 mb-5 px-4">
        {tracks.map((t, i) => (
          <Button
            key={t.id}
            onClick={() => switchTrack(i)}
            className={activeTrack === i ? "active" : ""}
          >
            {iconMap[t.icon]}
            <span className="hidden sm:inline">{t.name}</span>
            <span className="sm:hidden">{t.name.split(" ")[0]}</span>
          </Button>
        ))}
      </div>

      {/* NAVIGATION ARROWS */}
      <div className="relative z-10 flex justify-center gap-3 mb-6">
        <Button variant="ghost" onClick={prevTrack} aria-label="Previous track">
          <ChevronLeft className="h-5 w-5" />
        </Button>

        {/* Track indicator dots */}
        <div className="flex items-center gap-2">
          {tracks.map((_, i) => (
            <button
              key={i}
              onClick={() => switchTrack(i)}
              className={`h-2 rounded-full transition-all duration-300 ${i === activeTrack
                  ? "w-8 bg-gradient-to-r from-brand-green to-brand-blue"
                  : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                }`}
              aria-label={`Go to track ${i + 1}`}
            />
          ))}
        </div>

        <Button variant="ghost" onClick={nextTrack} aria-label="Next track">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* TECH CAROUSEL */}
      <div
        className="relative z-10"
        style={{
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? "translateY(20px)" : "translateY(0)",
          transition: "all 0.3s ease-out",
        }}
      >
        <TechCarousel technologies={track.technologies} />
      </div>

      {/* ROADMAP */}
      <div
        className="relative z-10"
        style={{
          opacity: isTransitioning ? 0 : 1,
          transition: "opacity 0.3s ease-out",
        }}
      >
        <RoadmapTimeline
          milestones={track.milestones}
          trackId={track.id}
          color={track.color}
        />
      </div>

      {/* CTA SECTION */}
      <div
        ref={ctaRef}
        className="relative z-10 mx-auto max-w-4xl px-4 pb-20"
        style={{
          opacity: ctaVisible ? 1 : 0,
          transform: ctaVisible ? "translateY(0)" : "translateY(50px)",
          transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className="glass-card rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
          {/* Decorative sparkle */}
          <Sparkles className="absolute top-6 right-6 h-6 w-6 text-brand-green/40 animate-float" />
          <Sparkles className="absolute bottom-6 left-6 h-5 w-5 text-brand-blue/30 animate-float" style={{ animationDelay: "-1.5s" }} />

          <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-black text-foreground mb-8 relative z-10">
            All internships are{" "}
            <span className="text-gradient-reverse">12 weeks</span>
            {" "}in duration
          </h3>

          {/* Feature cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            {features.map((item, i) => (
              <FeatureCard
                key={i}
                icon={item.icon}
                label={item.label}
                delay={i * 100}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InternshipTracks;
